import { Component, Input, OnInit, OnDestroy, ChangeDetectionStrategy, ViewEncapsulation, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MediaService } from '../../../shared/services/media.service';
import { BaseAppMComponent } from '../../../shared/base-components/base-app-m.component';
import { GlobalsService } from '../../../shared/services/globals.service';
import { DQOIReferenceDataQuery } from '@app/cp/dqoi/state/dqoi-reference-data.query';
import { FeatureFlagService } from '@app/cp/shared/services/feature-flag.service';
import { Observable, Subject, BehaviorSubject, of} from 'rxjs';
import { takeUntil, catchError, finalize } from 'rxjs/operators';
import { FileInfo, FileRestrictions } from '@progress/kendo-angular-upload';
import { PhotoBus } from '../../../shared/models/PhotoBus';
import { ConfirmOutput } from '../../../shared/models/ConfirmOutput';
import { CRUDActions } from '../../../shared/enums/CRUDActions';
import { CPDialogResult } from '../../../shared/enums/CPDialogResult';

@Component({
  selector: 'photo-spinner',
  templateUrl: './photo-spinner.component.html',
  styleUrls: ['./photo-spinner.component.scss'],
  encapsulation: ViewEncapsulation.Emulated,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PhotoSpinnerComponent extends BaseAppMComponent<null, MediaService, null, null> implements OnInit, OnDestroy {

  @Input() showForm = false;
  @Input() readOnly = false;
  @Input() photoType = 'Vehicle';
  @Input() urlStart = 'Vehicle';
  @Input() parentId: string;

  private destroy$ = new Subject<void>();
  private photosSubject = new BehaviorSubject<any[]>([]);
  photos$ = this.photosSubject.asObservable();

  activeIndex = 0;
  loadingThings = false;
  addPhoto = false;
  imageFail = false;

  photoForm: FormGroup;
  files: Array<FileInfo> = [];
  photoInfo: FileInfo[] = [];
  myFiles1: Array<FileInfo> = [];

  photoBlobs: { [key: string]: string } = {};
  hasPhoto = false;
  partPhoto: any;
  deleteId: string;
  
  private _loadingPhotos: { [key: string]: boolean } = {};

  height = (window.innerHeight - 200).toString() + 'px';
  uploadRestrictions: FileRestrictions = {
    allowedExtensions: ['.jpg', '.png', '.jpeg', '.gif']
  };

  constructor(
    globals: GlobalsService,
    featureFlagService: FeatureFlagService,
    private mediaSrvc: MediaService,
    qry_rd: DQOIReferenceDataQuery,
    private fb: FormBuilder,
    private cd: ChangeDetectorRef
  ) {
    super(globals, featureFlagService, qry_rd);
    this.initForm();
  }

  private initForm(): void {
    this.photoForm = this.fb.group({
      imageName: ['', Validators.required],
      description: [''],
      visibleToCustomers: [true],
      sequenceNumber: [0],
      photoCategoryId: ['52429EF0-6C30-483E-B4FF-5A00F8BE9BCB']
    });
  }

  @Input() set photos(value: any[]) {
    const photos = value || [];
    this.photosSubject.next(photos);

    if (photos.length > 0) {
      setTimeout(() => {
        this.preloadPhotos();
      }, 0);
    }
  }

  get photos(): any[] {
    return this.photosSubject.value;
  }

  ngOnInit(): void {
    super.ngOnInit && super.ngOnInit();
    this.preloadPhotos();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    super.ngOnDestroy && super.ngOnDestroy();
  }

  newPhoto(): void {
    this.photoForm.reset({
      visibleToCustomers: true,
      photoCategoryId: '52429EF0-6C30-483E-B4FF-5A00F8BE9BCB'
    });
    this.addPhoto = true;
    this.photoInfo = [];
    this.hasPhoto = false;
    this.partPhoto = null;
  }

  closePhoto(): void {
    this.addPhoto = false;
  }

  savePhoto(): void {
    if (this.photoInfo.length <= 0) {
      return;
    }
  
    this.loadingThings = true;
    const busObj = new PhotoBus();
    busObj.setFromForm(this.photoForm.value);
    busObj.parentId = this.parentId;
    busObj.photoCategoryId = '52429EF0-6C30-483E-B4FF-5A00F8BE9BCB';
  
    this.mediaSrvc.uploadPhoto(this.photoInfo, this.photoInfo[0].name, busObj, this.photoType)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.loadingThings = false;
          this.cd.detectChanges(); 
        })
      )
      .subscribe(
        data => this.inventoryPhotoSaved(data),
        err => this.saveError(err)
      );
  }

  deleteCurPhoto(deleteId: string): void {
    if (!this.photos || this.photos.length === 0) {
      return; 
    }
    
    this.deleteId = deleteId;
    this.cnfShow('Are you sure you want to delete this photo', CRUDActions.Delete, null, 'Delete Photo', 'CHSTATUS', false);
  }

  confirmDialog(data: ConfirmOutput): void {
    if (data.dialogResult === CPDialogResult.ok && data.action === CRUDActions.Delete) {
      this.deletePhoto(this.deleteId);
    } else {
      super.confirmDialog(data);
    }
  }

  deletePhoto(photoId: string): void {
    this.loadingThings = true;
  
    this.mediaSrvc.deletePhotos(this.photoType, photoId)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.loadingThings = false;
          this.cd.detectChanges();
        })
      )
      .subscribe(
        data => {
          if (!data.saveError) {
            const photos = [...this.photos];
            const index = photos.findIndex(item => item.photoId === photoId);
            if (index !== -1) {
              photos.splice(index, 1);
              this.photosSubject.next(photos);
              this.addPhoto = false;
              this.activeIndex = Math.max(0, Math.min(this.activeIndex, photos.length - 1));
              
              setTimeout(() => {
                this.cd.detectChanges();
                this.cd.markForCheck();
              }, 0);
            }
          } else {
            this.saveError(data.message);
          }
        },
        err => {
          this.saveError(err);
        }
      );
  }

  preloadPhotos(): void {
    const photos = this.photos;
    if (!photos || photos.length === 0) return;

    this.activeIndex = Math.min(this.activeIndex, photos.length - 1);

    photos.forEach(photo => {
      if (photo.fileName) {
        this.getOrLoadPhotoUrl(photo.fileName);
      }
    });
  }

  getOrLoadPhotoUrl(filename: string): string {
    if (filename && this.photoBlobs[filename]) {
      return this.photoBlobs[filename];
    }

    if (filename && !this.photoBlobs[filename] && !this._loadingPhotos[filename]) {
      this._loadingPhotos[filename] = true;
      const url = this.getPhotoApiUrl(filename);
      
      this.mediaSrvc.getImageWithAuth(url)
        .pipe(
          takeUntil(this.destroy$),
          catchError((err) => {
            console.error(err);
            this.imageFail = true;
            delete this._loadingPhotos[filename];
            return of(null);
          }),
          finalize(() => {
            delete this._loadingPhotos[filename];
          })
        )
        .subscribe(blob => {
          if (blob) {
            const reader = new FileReader();
            reader.onloadend = () => {
              this.photoBlobs[filename] = reader.result as string;
              this.cd.detectChanges();
            };
            reader.readAsDataURL(blob);
          }
        });
    }

    return 'assets/img/placeholder-image.svg';
  }

  getPhotoApiUrl(filename: string): string {
    if (filename) {
      return this.urlStart + filename;
    }
    return '';
  }

  public selectEventHandler(e: any): void {
    this.hasPhoto = false;
    this.photoInfo = [];

    if (e.files && e.files.length > 0) {
      const file = e.files[0];
      if (!file.validationErrors) {
        this.photoInfo.push(file);

        const reader = new FileReader();
        reader.onload = (event) => {
          setTimeout(() => {
            this.partPhoto = event.target?.result;
            this.hasPhoto = true;

            this.cd.detectChanges();
            this.cd.markForCheck();
          }, 0);
        };
        reader.readAsDataURL(file.rawFile);
        this.files = e.files;
        this.setDirty();
      }
    }
  }

  public clearEventHandler(): void {
    this.photoInfo = [];
  }

  public removeEventHandler(): void {
  }

  onImgError(): void {
    this.imageFail = true;
  }

  inventoryPhotoSaved(data: any): void {
    if (!data.saveError) {
      const newPhoto = new PhotoBus();
      newPhoto.initialize(data.primaryReturn);
      newPhoto.photoId = data.primaryId;

      const photos = [...this.photos, newPhoto];
      this.photosSubject.next(photos);

      if (newPhoto.fileName) {
        this.getOrLoadPhotoUrl(newPhoto.fileName);
      }

      this.addPhoto = false;
      this.activeIndex = photos.length - 1;
      
      setTimeout(() => {
        this.cd.detectChanges();
        this.cd.markForCheck();
      }, 0);
    } else {
      this.saveError(data.message);
    }
  }
}